/* ============================================================
   storage — transfers.

   Uploads are chunked and resumable: the server keeps a session
   with a byte count, and answers a mis-aimed chunk with the
   offset it actually wants. So a dropped connection is not a
   failed upload, it is a paused one — retry picks up from the
   server's number, never from a guess.

   What can go wrong, and what each case does:
     • network drops        → retry with backoff, then "paused"
     • drive unmounts (503) → retry; the message names the drive
     • no room (413)        → stop, say how much was needed
     • token expires (401)  → stop the queue, the shell re-logs in
     • the tab closes       → the server GCs the session after 2h
     • cancel               → session deleted server-side too

   Downloads stream through memory so the panel can show real
   progress, but only up to STREAM_LIMIT. Past that the browser's
   own downloader takes it: a 20 GB blob in a tab is a crash, not
   a progress bar.
   ============================================================ */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, AuthExpired } from './api';
import { sha256Slice } from './sha256';
import { markUnreachable } from './direct';

const CONCURRENCY = 3;
/**
 * How many downloads run at once.
 *
 * The server allows two connections per address so that parallel downloads
 * cannot multiply past its rate ceiling — so firing one request per selected
 * file, which is what "Download" on a five-file selection used to do, would
 * have the third onwards refused. Queue them here instead: the limit is the
 * server's, and the client should respect it rather than discover it.
 *
 * It is also simply better. Four files sharing one pipe all finish at the end;
 * two at a time means the first two are usable while the rest wait.
 */
const DOWNLOAD_CONCURRENCY = 2;

/** Thrown when the server says it is already streaming its limit to us. */
class TooBusy extends Error {}
const MAX_ATTEMPTS = 5;
const STREAM_LIMIT = 512 * 1024 * 1024;      // 512 MB

let seq = 0;
const nextId = () => `t${++seq}`;

const backoff = (attempt) => Math.min(15000, 500 * 2 ** attempt) + Math.random() * 300;
const sleep = (ms, signal) => new Promise((resolve, reject) => {
  const t = setTimeout(resolve, ms);
  signal?.addEventListener('abort', () => { clearTimeout(t); reject(new DOMException('aborted', 'AbortError')); }, { once: true });
});

/* ── the direct route (direct.js) ───────────────────────────────────────
   A chunk sent straight to a drive's agent. Errors come back in the same
   shape as the server's, so the loop below treats both routes alike; a
   network failure is status 0, which the loop answers by going the usual
   way instead. */
async function directChunk(direct, uid, offset, blob, signal) {
  let res;
  try {
    res = await fetch(`${direct.url}/u/${uid}/chunk`, {
      method: 'PUT', body: blob, signal,
      headers: { 'X-Ticket': direct.ticket, 'X-Offset': String(offset) },
    });
  } catch (e) {
    if (e.name === 'AbortError') throw e;
    throw new ApiError(0, { error: 'The drive stopped answering directly' });
  }
  let body = {};
  try { body = await res.json(); } catch { /* not json */ }
  if (!res.ok) throw new ApiError(res.status, body);
  return body;
}

async function directFinish(direct, uid, signal) {
  const res = await fetch(`${direct.url}/u/${uid}/finish`, {
    method: 'POST', signal, headers: { 'X-Ticket': direct.ticket },
  });
  if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
  return (await res.json()).receipt;
}

export function useTransfers(token, { onUploaded, onError, directFor } = {}) {
  const directRef = useRef(directFor);
  directRef.current = directFor;
  const [items, setItems] = useState([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const queue = useRef([]);
  const running = useRef(0);
  const controllers = useRef(new Map());
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const patch = useCallback((id, fields) => {
    setItems((list) => list.map((it) => (it.id === id ? { ...it, ...fields } : it)));
  }, []);

  // runUpload starts the next item when it finishes, and pump starts
  // runUpload: a cycle. The ref is the seam.
  const pumpRef = useRef(() => {});

  /* ── pacing ───────────────────────────────────────────────────────────
     nginx can pace a RESPONSE, so downloads are limited on the server. A
     REQUEST body has no equivalent — nothing between the browser and the
     socket will slow an upload down — so an upload is only polite if the
     uploader chooses to be, and the server publishes the rate it would like
     rather than the frontend hardcoding one that drifts from it.

     A token bucket, shared by every upload in flight: two files at once get
     half the rate each, not the rate each. It starts empty of debt and only
     ever waits AFTER a chunk, so the first chunk is never delayed. */
  const pace = useRef({ bps: 0, debtUntil: 0 });
  const [limits, setLimits] = useState(null);

  useEffect(() => {
    let live = true;
    api.limits()
      .then((l) => {
        if (!live) return;
        pace.current.bps = Number(l?.upload_bps) || 0;
        setLimits(l);
      })
      // No limits endpoint (an older server) means no pacing, which is the
      // behaviour this replaces — not a failure.
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const waitForBudget = useCallback(async (bytes) => {
    const bps = pace.current.bps;
    if (!bps || bytes <= 0) return;
    const now = Date.now();
    const start = Math.max(now, pace.current.debtUntil);
    const costMs = (bytes / bps) * 1000;
    pace.current.debtUntil = start + costMs;
    const wait = start - now;
    // Under the limit already: the network is slower than the ceiling, which
    // is the common case and costs nothing.
    if (wait <= 0) return;
    await new Promise((r) => setTimeout(r, Math.min(wait, 5000)));
  }, []);

  /* ── uploads ─────────────────────────────────────────────────────────── */

  const runUpload = useCallback(async (item) => {
    const ctrl = new AbortController();
    controllers.current.set(item.id, ctrl);
    const t = tokenRef.current;
    const started = Date.now();
    let sent = 0;
    try {
      patch(item.id, { state: 'running', error: null });
      /* Resume, not restart. A session this person left unfinished for the
         same file (a closed lid, a reload, Resume after a drop) carries on
         from what the server already has — once the file here proves to be
         that file: three samples of what was sent, hashed on both sides. */
      // The direct route, when this drive has one for this person and it
      // answers from here (direct.js). Chosen per upload, and given up on
      // silently mid-way if the agent stops answering.
      const base = await (directRef.current?.(item.drive) || Promise.resolve(null));
      let session = await api.uploadStart(t, item.name, item.file.size, item.path, item.drive, true, !!base);
      let offset = 0;
      if (session.received > 0 && Array.isArray(session.verify)) {
        let same = false;
        try {
          const hashes = [];
          for (const v of session.verify) hashes.push(await sha256Slice(item.file, v.offset, v.length));
          same = (await api.uploadVerify(t, session.upload_id, hashes)).ok;
        } catch { same = false; }
        if (same) {
          offset = session.received;
          sent = 0;
          patch(item.id, { sent: offset, resumedFrom: offset });
        } else {
          session = await api.uploadStart(t, item.name, item.file.size, item.path, item.drive, false, !!base);
        }
      }
      let direct = session.direct || null;
      const normalChunk = session.chunk_size || 5 * 1024 * 1024;
      let attempt = 0;
      patch(item.id, { uploadId: session.upload_id, route: direct ? 'direct' : null });

      while (offset < item.file.size) {
        if (ctrl.signal.aborted) throw new DOMException('aborted', 'AbortError');
        const end = Math.min(offset + (direct ? direct.chunk_size : normalChunk), item.file.size);
        try {
          // Pacing protects the uploader's internet link; the direct route
          // does not use it.
          if (!direct) await waitForBudget(end - offset);
          const res = direct
            ? await directChunk(direct, session.upload_id, offset, item.file.slice(offset, end), ctrl.signal)
            : await api.uploadChunk(t, session.upload_id, offset, item.file.slice(offset, end), ctrl.signal);
          sent += end - offset;
          offset = res.received;
          attempt = 0;
          const elapsed = (Date.now() - started) / 1000;
          const rate = elapsed > 0.3 ? sent / elapsed : 0;
          patch(item.id, {
            sent: offset,
            rate,
            eta: rate > 0 ? (item.file.size - offset) / rate : 0,
          });
        } catch (e) {
          if (e.name === 'AbortError') throw e;
          if (e instanceof AuthExpired) throw e;
          // The server knows the true offset; a 409 is a correction, not a
          // failure. Anything retryable gets a backoff, then gives up as
          // "paused" so the person can resume rather than start over.
          if (e instanceof ApiError && e.status === 409 && typeof e.body.expected === 'number') {
            offset = e.body.expected;
            continue;
          }
          // The agent stopped answering (the laptop left the house, the HP
          // restarted): carry on the usual way from where it got to. The
          // server reads that from the file itself.
          if (direct && e instanceof ApiError && (e.status === 0 || e.status >= 500 || e.status === 403)) {
            markUnreachable(direct.url);
            direct = null;
            patch(item.id, { route: null });
            const again = await api.uploadStart(t, item.name, item.file.size, item.path, item.drive, false, false, session.upload_id);
            if (again.upload_id === session.upload_id) offset = again.received || 0;
            else { session = again; offset = 0; }
            continue;
          }
          const retryable = e instanceof ApiError && (e.status === 0 || e.status === 503 || e.status >= 500);
          if (retryable && attempt < MAX_ATTEMPTS) {
            attempt += 1;
            patch(item.id, { state: 'retrying', error: e.message, attempt });
            await sleep(backoff(attempt), ctrl.signal);
            patch(item.id, { state: 'running' });
            continue;
          }
          throw e;
        }
      }
      // Sent directly: the agent hashes the file where it lies and signs the
      // result, so the server does not read the bytes back to check them.
      let receipt = null;
      if (direct) {
        patch(item.id, { state: 'running', error: null, verifying: true });
        try { receipt = await directFinish(direct, session.upload_id, ctrl.signal); } catch (e) {
          if (e.name === 'AbortError') throw e;
          receipt = null;                // the server hashes it instead
        }
      }
      const done = await api.uploadCommit(t, session.upload_id, receipt);
      patch(item.id, { state: 'done', sent: item.file.size, rate: 0, eta: 0, result: done });
      onUploaded?.(done);
    } catch (e) {
      if (e.name === 'AbortError') {
        patch(item.id, { state: 'cancelled' });
      } else if (e instanceof AuthExpired) {
        patch(item.id, { state: 'paused', error: 'Session expired — sign in to resume' });
      } else {
        patch(item.id, {
          // Anything the network or the server did (a drop, a restart, a 5xx)
          // is PAUSED — resumable, and resumed automatically — not failed.
          state: e instanceof ApiError && (e.status === 0 || e.status >= 500) ? 'paused' : 'failed',
          error: e.message,
        });
        onError?.(e);
      }
    } finally {
      controllers.current.delete(item.id);
      running.current -= 1;
      pumpRef.current();
    }
  }, [patch, onUploaded, onError, waitForBudget]);

  const pump = useCallback(() => {
    while (running.current < CONCURRENCY && queue.current.length) {
      const item = queue.current.shift();
      const live = itemsRef.current.find((x) => x.id === item.id);
      if (live && live.state === 'cancelled') continue;
      running.current += 1;
      runUpload(item);
    }
  }, [runUpload]);
  pumpRef.current = pump;

  /** Queue files. `entries` is [{file, relPath}] so a dropped folder keeps
   *  its shape; the server creates the folder chain on commit. */
  const upload = useCallback((entries, { drive, path }) => {
    const queued = entries
      .filter((e) => e.file && e.file.size > 0)
      .map((e) => {
        const sub = (e.relPath || '').split('/').slice(0, -1).filter(Boolean);
        const dest = sub.length ? `${path === '/' ? '' : path}/${sub.join('/')}` : path;
        return {
          id: nextId(), kind: 'upload', name: e.file.name, file: e.file,
          drive, path: dest || '/', size: e.file.size, sent: 0,
          state: 'queued', error: null, rate: 0, eta: 0, attempt: 0,
        };
      });
    const empty = entries.filter((e) => e.file && e.file.size === 0);
    const skipped = empty.map((e) => ({
      id: nextId(), kind: 'upload', name: e.file.name, drive, path,
      size: 0, sent: 0, state: 'failed', error: 'Empty file — nothing to store',
    }));
    setItems((list) => [...queued, ...skipped, ...list]);
    queue.current.push(...queued);
    pump();
    return queued.length;
  }, [pump]);

  /* ── downloads ───────────────────────────────────────────────────────── */

  const runDownload = useCallback(async (file, existingId = null) => {
    const t = tokenRef.current;
    // The direct route: a link to the file on its drive's own box.
    let directHref = null;
    try {
      if (await (directRef.current?.(file.drive) || Promise.resolve(null))) {
        directHref = (await api.fileDirect(t, file.id)).url;
      }
    } catch { directHref = null; }
    if (file.size > STREAM_LIMIT) {
      // Hand it to the browser, which streams to disk and survives a reload.
      const a = document.createElement('a');
      a.href = directHref || api.directUrl(t, file.id);
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setItems((list) => [{
        id: nextId(), kind: 'download', name: file.original_name, size: file.size,
        sent: file.size, state: 'handed-off', drive: file.drive, route: directHref ? 'direct' : null,
      }, ...list]);
      return;
    }
    const id = existingId || nextId();
    const ctrl = new AbortController();
    controllers.current.set(id, ctrl);
    if (existingId) {
      patch(id, { state: 'running', sent: 0, error: null });
    } else {
      setItems((list) => [{
        id, kind: 'download', name: file.original_name, size: file.size, sent: 0,
        state: 'running', drive: file.drive, fileRef: file,
      }, ...list]);
    }
    const started = Date.now();
    patch(id, { route: directHref ? 'direct' : null });
    try {
      let res = null;
      if (directHref) {
        try { res = await fetch(directHref, { signal: ctrl.signal }); } catch (e) {
          if (e.name === 'AbortError') throw e;
          res = null;
        }
        if (!res || !res.ok) {
          // Gone from here since the check: the usual way, no fuss.
          markUnreachable(directHref.split('/d/')[0]);
          res = null;
          patch(id, { route: null });
        }
      }
      if (!res) res = await fetch(api.streamUrl(file.id), {
        headers: { Authorization: `Bearer ${t}` }, signal: ctrl.signal,
      });
      if (res.status === 429) {
        // The server is already streaming as much as it will to this address.
        // That is a queue instruction, not a failure — wait and take the next
        // slot rather than telling someone their download broke.
        throw new TooBusy();
      }
      if (!res.ok) {
        let body = {};
        try { body = await res.json(); } catch { /* not json */ }
        throw new ApiError(res.status, body);
      }
      const total = Number(res.headers.get('Content-Length')) || file.size;
      const reader = res.body.getReader();
      const parts = [];
      let got = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        parts.push(value);
        got += value.length;
        const elapsed = (Date.now() - started) / 1000;
        const rate = elapsed > 0.3 ? got / elapsed : 0;
        patch(id, { sent: got, size: total, rate, eta: rate > 0 ? (total - got) / rate : 0 });
      }
      const url = URL.createObjectURL(new Blob(parts, { type: file.mime_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      patch(id, { state: 'done', sent: total, rate: 0, eta: 0 });
    } catch (e) {
      if (e.name === 'AbortError') patch(id, { state: 'cancelled' });
      else if (e instanceof TooBusy) {
        // Back to the queue. Capped attempts, because a server that says this
        // forever is a server with a problem, and retrying forever hides it.
        const tries = (retries.current.get(id) || 0) + 1;
        retries.current.set(id, tries);
        if (tries > MAX_ATTEMPTS) {
          patch(id, { state: 'failed', error: 'The server is busy — try again in a moment' });
        } else {
          patch(id, { state: 'queued', sent: 0, rate: 0, eta: 0 });
          setTimeout(() => {
            dlQueue.current.push({ file, id });
            dlPump.current();
          }, 800 * tries);
        }
      } else if (e instanceof TypeError) {
        // "Failed to fetch" is the browser refusing the response, not the
        // server refusing the request — a missing CORS header, an extension,
        // an offline blip. The plain-navigation download is not subject to
        // any of that, so fall back to it rather than reporting a failure
        // the user can do nothing about.
        const a = document.createElement('a');
        a.href = api.directUrl(tokenRef.current, file.id);
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        patch(id, { state: 'handed-off', sent: file.size, size: file.size, rate: 0, eta: 0 });
      } else {
        patch(id, { state: 'failed', error: e.message || 'Download failed' });
        onError?.(e);
      }
    } finally {
      controllers.current.delete(id);
    }
  }, [patch, onError]);

  /* A queue in front of the downloader, holding to the server's connection
     limit. Handed-off downloads (the browser's own, for huge files) do not go
     through it: those are the browser's connections to manage, not ours. */
  const dlQueue = useRef([]);
  const dlRunning = useRef(0);
  const retries = useRef(new Map());
  const dlPump = useRef(() => {});
  dlPump.current = () => {
    while (dlRunning.current < DOWNLOAD_CONCURRENCY && dlQueue.current.length) {
      const job = dlQueue.current.shift();
      dlRunning.current += 1;
      runDownload(job.file, job.id).finally(() => {
        dlRunning.current -= 1;
        dlPump.current();
      });
    }
  };

  const download = useCallback((file) => {
    if (file.size > STREAM_LIMIT) return runDownload(file);   // browser's problem
    // The row appears immediately, queued. A batch of ten shows ten rows with
    // two moving, which is what is actually happening — rather than ten rows
    // claiming to be running while the server refuses eight of them.
    const id = nextId();
    setItems((list) => [{
      id, kind: 'download', name: file.original_name, size: file.size,
      sent: 0, state: 'queued', drive: file.drive, fileRef: file,
    }, ...list]);
    dlQueue.current.push({ file, id });
    dlPump.current();
    return Promise.resolve();
  }, [runDownload]);

  /* ── controls ────────────────────────────────────────────────────────── */

  const cancel = useCallback(async (id) => {
    const item = itemsRef.current.find((x) => x.id === id);
    controllers.current.get(id)?.abort();
    queue.current = queue.current.filter((q) => q.id !== id);
    patch(id, { state: 'cancelled' });
    if (item?.uploadId) {
      try { await api.uploadCancel(tokenRef.current, item.uploadId); } catch { /* already gone */ }
    }
  }, [patch]);

  const retry = useCallback((id) => {
    const item = itemsRef.current.find((x) => x.id === id);
    if (!item) return;
    if (item.kind === 'download' && item.fileRef) { download(item.fileRef); return; }
    if (!item.file) return;
    patch(id, { state: 'queued', error: null, sent: 0, attempt: 0 });
    queue.current.push({ ...item, sent: 0 });
    pump();
  }, [download, patch, pump]);

  const retryRef = useRef(() => {});
  retryRef.current = retry;

  const cancelAll = useCallback(() => {
    itemsRef.current
      .filter((i) => ['queued', 'running', 'retrying', 'paused'].includes(i.state))
      .forEach((i) => cancel(i.id));
  }, [cancel]);

  const clearFinished = useCallback(() => {
    setItems((list) => list.filter((i) => ['queued', 'running', 'retrying', 'paused'].includes(i.state)));
  }, []);

  const active = items.filter((i) => ['queued', 'running', 'retrying'].includes(i.state));

  // Closing the tab mid-upload loses the queue; the browser's own prompt is
  // the only honest way to say so before it happens.
  useEffect(() => {
    if (!active.length) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [active.length]);

  /* Paused uploads come back by themselves: when the connection returns,
     when the laptop wakes (the tab becomes visible again), and once a minute
     while the tab is open. Each resumes from the bytes already on the
     server, so trying again costs nothing but a few samples. */
  useEffect(() => {
    const kick = () => {
      if (!navigator.onLine) return;
      itemsRef.current
        .filter((i) => i.kind === 'upload' && i.state === 'paused' && i.file && !/sign in/i.test(i.error || ''))
        .forEach((i) => retryRef.current(i.id));
    };
    const onVis = () => { if (!document.hidden) setTimeout(kick, 1500); };
    window.addEventListener('online', kick);
    document.addEventListener('visibilitychange', onVis);
    const id = setInterval(kick, 60000);
    return () => {
      window.removeEventListener('online', kick);
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(id);
    };
  }, []);

  /* The server only learns about an upload when it starts; until then the
     file is here, in this tab. Tell it what is waiting — every ten seconds
     while anything is, and once more with nothing when the queue empties —
     so an admin's "what is coming" includes it. A report goes stale on the
     server after 45s, which is what closing the tab looks like from there. */
  const reportedRef = useRef(false);
  useEffect(() => {
    const send = () => {
      const t = tokenRef.current;
      if (!t) return;
      const waiting = itemsRef.current.filter((i) => i.kind === 'upload' && i.state === 'queued');
      if (!waiting.length && !reportedRef.current) return;
      reportedRef.current = waiting.length > 0;
      api.reportQueue(t, {
        count: waiting.length,
        bytes: waiting.reduce((a, i) => a + (i.size || 0), 0),
        items: waiting.slice(0, 200).map((i) => ({ name: i.name, size: i.size, drive: i.drive, path: i.path })),
      }).catch(() => {});
    };
    send();
    const id = setInterval(send, 10000);
    return () => clearInterval(id);
  }, [items.length]);

  return { items, upload, download, cancel, cancelAll, retry, clearFinished, active, limits };
}

/** Walk a dropped DataTransfer into [{file, relPath}], folders included. */
export async function entriesFromDrop(dataTransfer) {
  const out = [];
  const items = Array.from(dataTransfer.items || []);
  const roots = items
    .map((i) => (i.webkitGetAsEntry ? i.webkitGetAsEntry() : null))
    .filter(Boolean);
  if (!roots.length) {
    return Array.from(dataTransfer.files || []).map((file) => ({ file, relPath: file.name }));
  }
  const walk = (entry, prefix) => new Promise((resolve) => {
    if (entry.isFile) {
      entry.file(
        (file) => { out.push({ file, relPath: prefix + file.name }); resolve(); },
        () => resolve(),                       // unreadable file: skip, don't stall the drop
      );
      return;
    }
    const reader = entry.createReader();
    const batch = () => reader.readEntries(async (list) => {
      if (!list.length) { resolve(); return; }
      await Promise.all(list.map((e) => walk(e, `${prefix}${entry.name}/`)));
      batch();                                  // readEntries pages at 100
    }, () => resolve());
    batch();
  });
  await Promise.all(roots.map((r) => walk(r, '')));
  return out;
}
